import axios from "../utils/axiosInstance";
import { Hotel, CreateHotelDTO, UpdateHotelDTO } from "../types/hotel.d";
import { User } from "../types/user.d";

/**
 * Service for managing hotel-related API calls.
 */
export const HotelService = {
  /**
   * Fetches all hotels from the API.
   * @returns {Promise<Hotel[]>} - List of hotels.
   * @throws Will throw an error if the request fails.
   */
  async getAllHotels(): Promise<Hotel[]> {
    try {
      const response = await axios.get<Hotel[]>("/Hotel", {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data || new Error("Network error");
    }
  },

  /**
   * Fetches users associated with a specific hotel by its ID.
   * @param {number} hotelId - ID of the hotel.
   * @returns {Promise<User[]>} - List of users linked to the hotel.
   * @throws Will throw an error if the request fails or if the hotel ID is invalid.
   */
  async getUsersByHotelId(hotelId: number): Promise<User[]> {
    try {
      const response = await axios.get<User[]>(`/Hotel/${hotelId}/users`, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });
      return response.data;
    } catch (error: any) {
      throw (
        error.response?.data ||
        new Error(
          `Erreur lors de la récupération des utilisateurs pour l'hôtel ID ${hotelId}`
        )
      );
    }
  },

  /**
   * Retrieves a specific hotel by its ID.
   * @param {number} id - ID of the hotel.
   * @returns {Promise<Hotel>} - Hotel object.
   * @throws Will throw an error if the request fails or if the hotel ID is invalid.
   */
  async getHotelById(id: number): Promise<Hotel> {
    try {
      const response = await axios.get<Hotel>(`/Hotel/${id}`, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });
      return response.data;
    } catch (error: any) {
      throw (
        error.response?.data ||
        new Error(`Erreur lors de la récupération de l'hôtel ID ${id}`)
      );
    }
  },

  /**
   * Creates a new hotel using the provided data.
   * @param {CreateHotelDTO} hotelData - Data for the new hotel.
   * @returns {Promise<CreateHotelDTO>} - Newly created hotel data.
   * @throws Will throw an error if the creation fails.
   */
  async createHotel(hotelData: CreateHotelDTO): Promise<Hotel> {
    try {
      const response = await axios.post<Hotel>("/Hotel", hotelData, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });
      return response.data;
    } catch (error: any) {
      throw (
        error.response?.data ||
        new Error("Erreur lors de la création de l’hôtel")
      );
    }
  },

  /**
   * Updates an existing hotel with new data.
   * @param {number} hotelId - ID of the hotel to update.
   * @param {UpdateHotelDTO} hotelData - Updated data for the hotel.
   * @returns {Promise<Hotel>} - Updated hotel data.
   * @throws Will throw an error if the update fails.
   */
  async updateHotel(
    hotelId: number,
    hotelData: UpdateHotelDTO
  ): Promise<Hotel> {
    try {
      const response = await axios.put<Hotel>(`/Hotel/${hotelId}`, hotelData, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });
      return response.data;
    } catch (error: any) {
      throw (
        error.response?.data || new Error("Erreur de mise à jour de l’hôtel")
      );
    }
  },

  /**
   * Deletes a hotel by its ID and optionally deletes associated contacts.
   * @param {number} hotelId - ID of the hotel to delete.
   * @param {boolean} alsoDeleteContacts - Flag indicating if associated contacts should also be deleted.
   * @returns {Promise<void>} - Resolves if the deletion is successful.
   * @throws Will throw an error if the deletion fails.
   */
  async deleteHotel(
    hotelId: number,
    alsoDeleteContacts: boolean
  ): Promise<void> {
    try {
      await axios.delete(`/Hotel/${hotelId}`, {
        data: { alsoDeleteContacts },
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });
    } catch (error: any) {
      throw (
        error.response?.data ||
        new Error("Erreur réseau lors de la suppression de l'hôtel")
      );
    }
  },

  async getUserCountByHotelId(hotelId: number): Promise<number> {
    try {
      const response = await axios.get<number>(
        `/Hotel/${hotelId}/users/count`,
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error: any) {
      throw (
        error.response?.data ||
        new Error(
          `Erreur lors de la récupération du nombre d'utilisateurs pour l'hôtel ID ${hotelId}`
        )
      );
    }
  },

  async getAllHotelsUserCount(): Promise<{ [key: number]: number }> {
    try {
      const response = await axios.get<{ [key: number]: number }>(
        `/Hotel/users/count/all`,
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        }
      );
      return response.data;
    } catch (error: any) {
      throw (
        error.response?.data ||
        new Error("Erreur lors de la récupération des contacts par hôtel")
      );
    }
  },
};

export default HotelService;
